import { BrandLockup } from '../components/BrandLockup.js'
import {
  boardExport,
  canDeleteBoard,
  CARD_PRIORITIES,
  CARD_PRIORITY_LABELS,
  STATUS_LABELS,
  STATUSES,
  MOSCOW_LABELS,
  MOSCOW_LEVELS,
} from '@blueprint/domain'
import { useEffect, useId, useRef, useState } from 'react'
import { Button } from '../components/controls.js'
import type { FilterIndex } from '../state/filter-index.js'
import { currentSnapshot, useBoardStore } from '../state/board-store.js'
import { exportBoardPdf, exportBoardPng } from '../features/boards/export-board.js'
import { BoardVersionPicker } from '../features/boards/BoardVersionPicker.js'
import { useCanRedo, useCanUndo, useRedoLabel, useUndoLabel } from '../state/selectors.js'
import { useSessionStore } from '../state/session-store.js'
import { useUiStore } from '../state/ui-store.js'
import { UserMenu } from './UserMenu.js'
import { BoardSourceLink } from '../features/mind-map/SourceLinks.js'
import styles from './TopBar.module.css'

export function TopBar({ filterIndex }: { filterIndex: FilterIndex }) {
  const viewingHistory = useBoardStore((state) => state.viewedVersion !== null)
  return (
    <header className={styles.bar}>
      <div className={styles.row}>
        <BoardControls />
        <BoardVersionPicker />
        <HistoryControls />
        <UserMenu />
      </div>
      <div className={styles.row}>
        {viewingHistory ? <span className={styles.counts}>Saved version · read only</span> : <SearchAndFilters index={filterIndex} />}
      </div>
    </header>
  )
}

function BoardControls() {
  const setSwitcherOpen = useUiStore((state) => state.setSwitcherOpen)
  const setView = useUiStore((state) => state.setView)
  const boardName = useBoardStore((state) => state.entities?.board.name ?? '')

  return (
    <>
      <button
        type="button"
        className={styles.brand}
        onClick={() => setView('boards')}
        title="Back to all boards"
      >
        <img className={styles.logo} src="/logo-mark.png" alt="" width={40} height={21} />
        <span className={styles.brandText}>
          <BrandLockup />
          <span className={styles.boardName}>{boardName}</span>
        </span>
      </button>

      <BoardSourceLink />

      <span className={styles.spacer} />

      <Button onClick={() => setSwitcherOpen(true)}>Switch…</Button>

      <BoardGearMenu />
    </>
  )
}

/** Blueprint's own export, which Import… reads back into a new board. */
function downloadJson(): void {
  const snapshot = currentSnapshot()
  if (!snapshot) return
  const payload = boardExport(snapshot)
  const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' })
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = `${snapshot.board.name.replace(/[^\w.-]+/g, '-').toLowerCase() || 'board'}.json`
  anchor.click()
  URL.revokeObjectURL(url)
}

/**
 * Actions on the board you are looking at. Board-list management lives in the Boards
 * view; this is for things that act on the open board. More entries to follow.
 */
function BoardGearMenu() {
  const activeBoardId = useBoardStore((state) => state.activeBoardId)
  const viewingHistory = useBoardStore((state) => state.viewedVersion !== null)
  const boardName = useBoardStore((state) => state.entities?.board.name ?? 'board')
  const [busy, setBusy] = useState(false)
  const setImportOpen = useUiStore((state) => state.setImportOpen)
  const setBoardNameDialog = useUiStore((state) => state.setBoardNameDialog)
  const openFindings = useBoardStore((state) =>
    state.entities ? Object.values(state.entities.findings ?? {}).filter((finding) => finding.status === 'open').length : 0,
  )
  const requestConfirm = useUiStore((state) => state.requestConfirm)
  // Delete is offered by the same rule the API enforces: the owner, or an editor of its project.
  const canDelete = useBoardStore((state) => {
    const summary = state.boards.find((entry) => entry.id === state.activeBoardId)
    if (!summary) return false
    return canDeleteBoard(summary.access, state.projects.find((project) => project.id === summary.projectId)?.role ?? null)
  })
  // During a Teamcentric Accounts trial no data leaves the tenant: no exports.
  const trial = useSessionStore((state) => state.trial)
  const [open, setOpen] = useState(false)
  const wrapRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const onPointerDown = (event: PointerEvent) => {
      if (event.target instanceof Node && wrapRef.current?.contains(event.target)) return
      setOpen(false)
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  /** Capture is slow on a large board, so the entries lock while one is running. */
  const runExport = async (capture: () => Promise<void>) => {
    setBusy(true)
    try {
      await capture()
    } catch (error) {
      // Surfaces in the board's existing error bar.
      useBoardStore.setState({
        error: error instanceof Error ? error.message : 'The board could not be exported.',
      })
    } finally {
      setBusy(false)
    }
  }

  const deleteActive = () => {
    setOpen(false)
    const state = useBoardStore.getState()
    const board = state.entities?.board
    if (!board) return
    const cards = Object.keys(state.entities?.cards ?? {}).length
    requestConfirm({
      title: 'Delete board',
      message: `"${board.name}" will be deleted permanently.`,
      consequence: `${cards} card${cards === 1 ? '' : 's'} will be deleted.`,
      confirmLabel: 'Delete board',
      undoable: false,
      onConfirm: () => void useBoardStore.getState().deleteBoard(board.id),
    })
  }

  return (
    <div className={styles.gearWrap} ref={wrapRef}>
      <button
        type="button"
        className={styles.gear}
        disabled={viewingHistory}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Board actions"
        title="Board actions"
        onClick={() => setOpen((value) => !value)}
      >
        {/* A toothed cog: straight radial spokes read as a sun, not a setting. */}
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2Z" />
          <circle cx="12" cy="12" r="3" />
        </svg>
      </button>

      {open ? (
        <div className={styles.gearMenu} role="menu">
          <button
            type="button"
            role="menuitem"
            className={styles.gearItem}
            onClick={() => {
              setOpen(false)
              setImportOpen(true)
            }}
          >
            Import…
          </button>
          {trial ? null : (
            <>
              <button
                type="button"
                role="menuitem"
                className={styles.gearItem}
                disabled={!activeBoardId}
                onClick={() => {
                  setOpen(false)
                  downloadJson()
                }}
              >
                Export JSON…
              </button>
              <button
                type="button"
                role="menuitem"
                className={styles.gearItem}
                disabled={!activeBoardId || busy}
                onClick={() => {
                  setOpen(false)
                  void runExport(() => exportBoardPng(boardName))
                }}
              >
                Export PNG…
              </button>
              <button
                type="button"
                role="menuitem"
                className={styles.gearItem}
                disabled={!activeBoardId || busy}
                onClick={() => {
                  setOpen(false)
                  void runExport(() => exportBoardPdf(boardName))
                }}
              >
                Export PDF…
              </button>
            </>
          )}
          <button
            type="button"
            role="menuitem"
            className={styles.gearItem}
            disabled={!activeBoardId}
            onClick={() => {
              setOpen(false)
              setBoardNameDialog('rename')
            }}
          >
            Rename board…
          </button>
          <button
            type="button"
            role="menuitem"
            className={styles.gearItem}
            disabled={!activeBoardId}
            onClick={() => {
              setOpen(false)
              useUiStore.getState().setContextOwner({ scope: 'board' })
            }}
          >
            Board architecture context…
          </button>
          <button
            type="button"
            role="menuitem"
            className={styles.gearItem}
            disabled={!activeBoardId}
            onClick={() => {
              setOpen(false)
              useUiStore.getState().setReconciliation({ targetId: null })
            }}
          >
            Reconciliation…{openFindings > 0 ? ` (${openFindings} open)` : ''}
          </button>
          {canDelete ? (
            <button
              type="button"
              role="menuitem"
              className={`${styles.gearItem} ${styles.gearItemDanger}`}
              disabled={!activeBoardId}
              onClick={deleteActive}
            >
              Delete board
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}

function HistoryControls() {
  const canUndo = useCanUndo()
  const canRedo = useCanRedo()
  const undoLabel = useUndoLabel()
  const redoLabel = useRedoLabel()
  const undo = useBoardStore((state) => state.undo)
  const redo = useBoardStore((state) => state.redo)

  return (
    <div className={styles.group}>
      <Button onClick={undo} disabled={!canUndo} title={undoLabel ? `Undo: ${undoLabel}` : 'Nothing to undo'}>
        Undo
      </Button>
      <Button onClick={redo} disabled={!canRedo} title={redoLabel ? `Redo: ${redoLabel}` : 'Nothing to redo'}>
        Redo
      </Button>
    </div>
  )
}

function SearchAndFilters({ index }: { index: FilterIndex }) {
  const filters = useUiStore((state) => state.filters)
  const setFilter = useUiStore((state) => state.setFilter)
  const clearFilters = useUiStore((state) => state.clearFilters)
  const searchId = useId()
  const statusId = useId()
  const priorityId = useId()
  const moscowId = useId()
  const ownerId = useId()
  const tagId = useId()
  const releaseId = useId()

  return (
    <>
      <label className={styles.srOnlyLabel} htmlFor={searchId}>
        Search cards
      </label>
      <input
        id={searchId}
        className={styles.search}
        type="search"
        placeholder="Search title and description"
        value={filters.search}
        onChange={(event) => setFilter('search', event.target.value)}
      />

      <label className={styles.srOnlyLabel} htmlFor={statusId}>
        Filter by status
      </label>
      <select
        id={statusId}
        className={styles.filter}
        value={filters.status}
        onChange={(event) => setFilter('status', event.target.value as typeof filters.status)}
      >
        <option value="">Any status</option>
        {STATUSES.map((status) => (
          <option key={status} value={status}>
            {STATUS_LABELS[status]}
          </option>
        ))}
      </select>

      <label className={styles.srOnlyLabel} htmlFor={priorityId}>
        Filter by priority
      </label>
      <select
        id={priorityId}
        className={styles.filter}
        value={filters.priority}
        onChange={(event) => setFilter('priority', event.target.value as typeof filters.priority)}
      >
        <option value="">Any priority</option>
        {CARD_PRIORITIES.map((priority) => (
          <option key={priority} value={priority}>
            {CARD_PRIORITY_LABELS[priority]}
          </option>
        ))}
      </select>

      <label className={styles.srOnlyLabel} htmlFor={moscowId}>
        Filter by MoSCoW
      </label>
      <select
        id={moscowId}
        className={styles.filter}
        value={filters.moscow}
        onChange={(event) => setFilter('moscow', event.target.value as typeof filters.moscow)}
      >
        <option value="">Any MoSCoW</option>
        {MOSCOW_LEVELS.map((level) => (
          <option key={level} value={level}>
            {MOSCOW_LABELS[level]}
          </option>
        ))}
      </select>

      <label className={styles.srOnlyLabel} htmlFor={ownerId}>
        Filter by owner
      </label>
      <input
        id={ownerId}
        className={styles.filter}
        list={`${ownerId}-options`}
        placeholder="Any owner"
        value={filters.owner}
        onChange={(event) => setFilter('owner', event.target.value)}
      />
      <datalist id={`${ownerId}-options`}>
        {index.facets.owners.map((value) => (
          <option key={value} value={value} />
        ))}
      </datalist>

      <label className={styles.srOnlyLabel} htmlFor={tagId}>
        Filter by tag
      </label>
      <select id={tagId} className={styles.filter} value={filters.tag} onChange={(event) => setFilter('tag', event.target.value)}>
        <option value="">Any tag</option>
        {index.facets.tags.map((value) => (
          <option key={value} value={value}>
            {value}
          </option>
        ))}
      </select>

      <label className={styles.srOnlyLabel} htmlFor={releaseId}>
        Filter by release
      </label>
      <input
        id={releaseId}
        className={styles.filter}
        list={`${releaseId}-options`}
        placeholder="Any release"
        value={filters.release}
        onChange={(event) => setFilter('release', event.target.value)}
      />
      <datalist id={`${releaseId}-options`}>
        {index.facets.releases.map((value) => (
          <option key={value} value={value} />
        ))}
      </datalist>

      <Button onClick={clearFilters} disabled={!index.active}>
        Clear filters
      </Button>

      <span className={styles.counts} data-testid="filter-counts">
        {index.active ? `${index.matching} of ${index.total} cards match` : `${index.total} cards`}
      </span>

      {index.active ? (
        <span className={styles.lockNote} data-testid="drag-locked">
          Card dragging is off while a filter is active. Editing still works.
        </span>
      ) : null}
    </>
  )
}
